import React, { useState } from "react";
import { Button, Form, Checkbox, message } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import InputCard from "../../../../../Components/Cards/InputCard";
import { createFuelVendorDocumentRequirement } from "../../../../../API/Admin/SystemConfiguration/FuelVendorDocuments";
import "./style.css";

const getErrorMessage = (data, fallback) => {
  if (!data) return fallback;
  if (data.message) return data.message;
  if (data.detail) return data.detail;

  const fieldErrors = Object.values(data)
    .flat()
    .filter((msg) => typeof msg === "string");

  return fieldErrors.length ? fieldErrors.join(" ") : fallback;
};

const CATEGORY_OPTIONS = [
  { label: "Legal", value: "legal" },
  { label: "Financial", value: "financial" },
  { label: "Compliance", value: "compliance" },
  { label: "Operational", value: "operational" },
  { label: "General", value: "general" },
];

const FORMAT_OPTIONS = [
  { label: "PDF", value: "pdf" },
  { label: "JPG", value: "jpg" },
  { label: "PNG", value: "png" },
  { label: "DOCX", value: "docx" },
];

const AddFuelVendorDocument = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const handleBack = () => {
    navigate("/system-configuration", {
      state: { activeTab: "Fuel Vendor Document Required" },
    });
  };

  const handleCancel = () => {
    form.resetFields();
  };

  const generalInformationFields = [
    {
      name: "documentName",
      label: "Document Name",
      type: "text",
      placeholder: "Enter Document Name",
      rules: [{ required: true, message: "Document name is required" }],
    },
    {
      name: "category",
      label: "Category",
      type: "select",
      placeholder: "Select Category",
      options: CATEGORY_OPTIONS,
      rules: [{ required: true, message: "Category is required" }],
    },
  ];

  const requirementLogicFields = [
    {
      name: "mandatoryAtOnboarding",
      label: "Mandatory at Onboarding",
      description: "User cannot proceed without uploading this.",
      type: "switch",
      fullWidth: true,
    },
    {
      name: "isRequired",
      label: "Required",
      description: "Marks this document as mandatory for approval.",
      type: "switch",
      fullWidth: true,
    },
    {
      name: "hasExpiry",
      label: "Has Expiry Date",
      description: "This document type can have an expiry date tracked.",
      type: "switch",
      fullWidth: true,
    },
  ];

  const handleSubmit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (error) {
      message.error("Please fill in all required fields.");
      return;
    }

    setSaving(true);
    try {
      const res = await createFuelVendorDocumentRequirement({
        document_name: values.documentName,
        category: values.category,
        required_at_onboarding: values.mandatoryAtOnboarding,
        is_required: values.isRequired,
        has_expiry: values.hasExpiry,
        allowed_formats: values.allowedFormat,
      });

      if (Math.floor(res?.status / 100) === 2) {
        message.success(
          getErrorMessage(res?.data, "Fuel Vendor document added successfully!"),
        );
        navigate("/system-configuration", {
          state: { activeTab: "Fuel Vendor Document Required" },
        });
      } else {
        message.error(getErrorMessage(res?.data, "Failed to add document."));
      }
    } catch (err) {
      message.error(
        getErrorMessage(err?.response?.data, "Failed to add document."),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="add-fuel-vendor">
      <div className="back-btn">
        <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
          Back
        </Button>
      </div>
      <div className="title">Add Document Requirement </div>
      <Form form={form} layout="vertical">
        <div className="input-cards-container">
          <InputCard
            title="General Information"
            fields={generalInformationFields}
          />

          <InputCard
            title="Requirement Logic"
            fields={requirementLogicFields}
          />

          <InputCard title="Allowed Formats">
            <Form.Item
              name="allowedFormat"
              label="Accepted File Type"
              rules={[
                { required: true, message: "Please select at least one format" },
              ]}
            >
              <Checkbox.Group options={FORMAT_OPTIONS} />
            </Form.Item>
          </InputCard>
        </div>

        <div className="btns-row">
          <Button onClick={handleCancel} disabled={saving}>
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleSubmit}
            className="save-btn"
            loading={saving}
            disabled={saving}
          >
            Add Fuel Vendor Document
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default AddFuelVendorDocument;
